import { useCallback } from 'react';

import { useInfiniteLoader } from '../useInfiniteLoader';
import { useGraphQuery } from '../useQuery/useGraphQuery';

import {
  UseQueryInfiniteListParams,
  UseQueryInfiniteListReturn,
} from './useQueryInfiniteList.decl';

import { OperationVariables } from '@apollo/client';

/**
 * A hook that run a query and fetch the next page when the container is scrolled to the bottom
 * @param query - Query to run
 * @param containerRef - Ref of the scrollable container
 * @param queryOptions  - option that applys
 * @param getMeta - funtion to return pagination related state
 * @param loaderThreshold - The threshold distance from the bottom of the content to trigger loading next page
 *
 * @returns query result
 */
export function useQueryInfiniteList<TData, TVariables = OperationVariables | undefined>({
  query,
  containerRef,
  queryOptions,
  getMeta,
  loaderThreshold = 100,
}: UseQueryInfiniteListParams<TData, TVariables>): UseQueryInfiniteListReturn<TData> {
  const { loading, data, fetchMore, error } = useGraphQuery<TData, TVariables>(query, {
    notifyOnNetworkStatusChange: true,
    ...queryOptions,
  });

  const onLoad = useCallback(() => {
    if (!data) {
      return;
    }

    const meta = getMeta(data);
    const { perPage, currentPage, total } = meta;
    const hasMore = currentPage * perPage < total;

    if (!hasMore) {
      return;
    }

    fetchMore({ variables: { ...queryOptions?.variables, page: currentPage + 1 } });
  }, [data, getMeta, fetchMore, queryOptions]);

  const { scrollAtBottom } = useInfiniteLoader({
    containerRef,
    threshold: loaderThreshold,
    skip: loading,
    onLoad,
  });

  return { loading, data, error, scrollAtBottom, onLoad };
}
